import { getTokenCookie } from '@root/utils/cookies/cookies';
import getConfig from 'next/config';
import { NextRouter } from 'next/router';

import { CursiveRecorder, isElectron } from './electron';

const { publicRuntimeConfig } = getConfig();
const CURSIVE_ROOT_URL = publicRuntimeConfig.cursiveRootUrl;

export const ZENDESK_SUPPORT_URL = 'https://support.scribehow.com/hc/en-us/requests/new';
export const SCRIBE_DESKTOP_ELECTRON_PROTOCOL = 'scribe-by://';
export const SCRIBE_DESKTOP_MACOS_PROTOCOL = 'scribehow://';

export const openInNewTab = (url: string) => window?.open(url, '_blank');

export const openUrl = (link) => {
  if (isElectron()) {
    CursiveRecorder().openURLInBrowser(link);
  } else {
    openInNewTab(link);
  }
};

export const transformForElectronApp = (url) =>
  url
    .replace('https://', SCRIBE_DESKTOP_ELECTRON_PROTOCOL)
    .replace('http://', SCRIBE_DESKTOP_ELECTRON_PROTOCOL)
    .replace('//scribehow.com', '//cursive.io');

export const transformForMacOSApp = (url) =>
  url.replace('https://', SCRIBE_DESKTOP_MACOS_PROTOCOL).replace('http://', SCRIBE_DESKTOP_MACOS_PROTOCOL);

export const getRootUrlForElectronApp = () => transformForElectronApp(CURSIVE_ROOT_URL);
export const getRootUrlForMacOSApp = () => transformForMacOSApp(CURSIVE_ROOT_URL);

export const openDesktopApp = (path: string, additionalQueryParams: Record<string, string> = {}, target = '_self'): void => {
  const { refresh, customUrls: singleTenantUrls } = getTokenCookie();
  let singleTenantUrlsParamString = '';
  let additionalQueryParamString = '';

  if (singleTenantUrls) {
    const frontendUrl = encodeURIComponent(singleTenantUrls.frontendUrl);
    const backendUrl = encodeURIComponent(singleTenantUrls.backendUrl);

    singleTenantUrlsParamString = `&frontendurl=${frontendUrl}&backendurl=${backendUrl}`;
  }
  for (const [param, value] of Object.entries(additionalQueryParams)) {
    if (!!param && !!value) {
      additionalQueryParamString += `&${param}=${value}`;
    }
  }
  window.open(`${getRootUrlForMacOSApp()}${path}?t=${refresh}${singleTenantUrlsParamString}${additionalQueryParamString}`, target);
};

export const removeCurrentUrlQueryParamWithoutReRendering = (router: NextRouter, queryParam: string): void => {
  const query = { ...router.query, [queryParam]: undefined };
  const validEntries = Object.entries(query).filter((entry) => !!entry[1]);
  let queryString = validEntries.map((item) => `${item[0]}=${item[1]}`).join('&');

  if (queryString) {
    queryString = `?${queryString}`;
  }

  const newUrl = `${router.pathname}${queryString}`;

  window.history.replaceState({ ...window.history.state, as: newUrl, url: newUrl }, '', newUrl);
};

// These URLs are ones that are visible to signed out users so a 401 is possible and totally fine.
export const isCurrentUrlWhiteListed = (): boolean => {
  let isWhitelisted = false;

  try {
    const currentUrl = window?.location?.href || '';

    isWhitelisted =
      (window !== undefined &&
        (currentUrl.includes('/shared/') ||
          currentUrl.includes('/page/') ||
          currentUrl.includes('/embed/') ||
          currentUrl.includes('/page-embed/') ||
          currentUrl.includes('/private-page-embed/') ||
          currentUrl.includes('/by/'))) ||
      currentUrl.includes('/community');
  } catch (ReferenceError) {
    // catches server-side error where `window` does not exists.
  }

  return isWhitelisted;
};

export const extractCapitalizedDomainName = (url: string): string => {
  // If url = https://www.figma.com/design/c4fW1ti4VwDqkXE7CRFgGN/Ask-Scribe?node-id=577-7185&node-type=canvas&m=dev
  // returns "Figma"
  try {
    const { hostname } = new URL(url);
    const domainParts = hostname.split('.');
    const lowercaseName = domainParts.length > 1 ? domainParts[domainParts.length - 2] : hostname;

    return lowercaseName.charAt(0).toUpperCase() + lowercaseName.slice(1).toLowerCase();
  } catch (error) {
    return '';
  }
};
